import {
    RadioGroup,
    FormControlLabel,
    Radio,
    Switch,
    Box,
} from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import React from "react";
import theme from "Theme/AppTheme";
import { DataSourcesStore } from "./stores/DataSourcesStore";
import { observer } from "mobx-react";
import { chatFileSchemaOptions, sourceFieldsByType } from "./helpers/constants";
import DataSourceContainer from "./components/DataSourceContainer";
import { SourceFileType } from "./types";
import ErrorHandler from "./components/ErrorHandler";
import InputContainer from "./components/InputContainer";

type DataSourceSetupProps = {
    store: DataSourcesStore;
};

const DataSourceSetup: React.FC<DataSourceSetupProps> = observer(
    ({ store }) => {
        const { setup, setSetup, hasError } = store;

        const updateSetup = (
            key: string,
            value: string | boolean | SourceFileType,
        ) => {
            setSetup({ ...setup, [key]: value });
        };

        const selectedType = sourceFieldsByType[setup.fileType];

        return (
            <DataSourceContainer heading="Setup">
                <ErrorHandler
                    hasError={hasError.setup}
                    errorMessage="Please enter a configuration name to continue."
                />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: theme.spacing(2),
                    }}
                >
                    {selectedType.includes("sourceType") && (
                        <InputContainer label="Source Type">
                            <RadioGroup
                                onChange={(e) => {
                                    updateSetup("sourceType", e.target.value);
                                }}
                                value={setup.sourceType}
                                sx={{ display: "flex", flexDirection: "row" }}
                            >
                                <FormControlLabel
                                    value="single"
                                    control={<Radio />}
                                    label="Single Metadata File"
                                    labelPlacement="end"
                                />

                                <FormControlLabel
                                    value="batch"
                                    control={<Radio />}
                                    label="Batch Metadata File"
                                    labelPlacement="end"
                                />
                            </RadioGroup>
                        </InputContainer>
                    )}

                    {selectedType.includes("configName") && (
                        <InputContainer label="Configuration Name">
                            <AcxMainTextField
                                fullWidth
                                id="datasources-configuration-name"
                                value={setup.configurationName}
                                placeholderText="Enter configuration name..."
                                onChange={(e) =>
                                    updateSetup(
                                        "configurationName",
                                        e.target.value,
                                    )
                                }
                                error={hasError.setup}
                                hideErrorIcon
                            />
                        </InputContainer>
                    )}

                    {selectedType.includes("fileType") && (
                        <InputContainer label="File Type">
                            <AcxMainTextField
                                fullWidth
                                isDisabled
                                id="datasources-file-type"
                                value={setup.fileType}
                            />
                        </InputContainer>
                    )}

                    {selectedType.includes("fileTypeExtension") && (
                        <InputContainer label="File Type Extension">
                            <AcxMainTextField
                                fullWidth
                                isDisabled
                                id="datasources-file-type-extension"
                                value={
                                    setup.chatFileSchemaHandler &&
                                    setup.sourceType === "single"
                                        ? `${setup.fileType}_CHAT`
                                        : setup.fileTypeExtension
                                }
                            />
                        </InputContainer>
                    )}

                    {selectedType.includes("namespace") && (
                        <InputContainer label="Namespace">
                            <AcxMainTextField
                                fullWidth
                                id="datasources-namespace"
                                value={setup.namespace}
                                placeholderText="Enter namespace..."
                                onChange={(e) =>
                                    updateSetup("namespace", e.target.value)
                                }
                            />
                        </InputContainer>
                    )}

                    {selectedType.includes("prefix") && (
                        <InputContainer label="Prefix">
                            <AcxMainTextField
                                fullWidth
                                id="datasources-prefix"
                                value={setup.prefix}
                                placeholderText="Enter prefix..."
                                onChange={(e) =>
                                    updateSetup("prefix", e.target.value)
                                }
                            />
                        </InputContainer>
                    )}

                    {selectedType.includes("chatFileSchemaHandler") &&
                        setup.sourceType === "single" && (
                            <InputContainer label="Chat File Schema Handler">
                                <AcxSelect
                                    fullWidth
                                    id="datasources-chat-file-schema-handler"
                                    options={chatFileSchemaOptions}
                                    labelField="label"
                                    valueField="value"
                                    placeholder="Select"
                                    defaultValue={chatFileSchemaOptions.find(
                                        (e) =>
                                            e.value ===
                                            setup.chatFileSchemaHandler,
                                    )}
                                    onChange={(e) =>
                                        updateSetup(
                                            "chatFileSchemaHandler",
                                            e.value,
                                        )
                                    }
                                />
                            </InputContainer>
                        )}

                    {selectedType.includes("delimiter") && (
                        <InputContainer label="Delimiter">
                            <AcxMainTextField
                                fullWidth
                                id="datasources-delimiter"
                                value={setup.delimiter}
                                placeholderText="Enter delimiter..."
                                onChange={(e) =>
                                    updateSetup("delimiter", e.target.value)
                                }
                            />
                        </InputContainer>
                    )}

                    {selectedType.includes("agentCodeRequired") && (
                        <InputContainer label="Agent Code Required">
                            <Switch
                                sx={{ width: 40, marginLeft: 0 }}
                                onChange={() =>
                                    updateSetup(
                                        "isAgentCodeRequired",
                                        !setup.isAgentCodeRequired,
                                    )
                                }
                                checked={setup.isAgentCodeRequired}
                            />
                        </InputContainer>
                    )}

                    {selectedType.includes("isDebug") &&
                        setup.sourceType === "batch" && (
                            <InputContainer label="Is Debug">
                                <Switch
                                    sx={{ width: 40, marginLeft: 0 }}
                                    onChange={() =>
                                        updateSetup("isDebug", !setup.isDebug)
                                    }
                                    checked={setup.isDebug}
                                />
                            </InputContainer>
                        )}
                </Box>
            </DataSourceContainer>
        );
    },
);

export default DataSourceSetup;
